import React from 'react';
import { FiCalendar, FiEdit2, FiTrash2, FiLock, FiTag, FiBook } from 'react-icons/fi';
import { formatAmount, formatSignedAmount } from '../utils/currency';
import './TransactionList.css';

/**
 * Reusable Transaction List Component
 * Renders as table on desktop and cards on mobile
 *
 * Props:
 * - transactions: array of transaction objects
 * - columns: array of column keys to show (e.g., ['date', 'ledger', 'category', 'description', 'type', 'amount', 'actions'])
 * - onEdit: function(txn) - called when edit is clicked
 * - onDelete: function(txn) - called when delete is clicked
 * - isTransactionLocked: function(txn) - returns true if transaction is locked
 * - getTypeInfo: function(type) - returns {label, icon, color, class} for transaction type
 * - formatDate: function(dateStr) - formats date string
 * - t: translation function
 * - toneEnabled: boolean
 * - showActions: boolean - whether to show edit/delete actions
 */
const TransactionList = ({
  transactions,
  columns = ['date', 'type', 'description', 'amount'],
  onEdit,
  onDelete,
  isTransactionLocked = () => false,
  getTypeInfo,
  formatDate,
  t = (key) => key,
  toneEnabled = false,
  showActions = false,
}) => {
  if (!transactions || transactions.length === 0) {
    return null;
  }

  const renderMobileCard = (txn) => {
    // Pass full transaction to getTypeInfo for direction-aware display (e.g., internal transfers)
    const typeInfo = getTypeInfo ? getTypeInfo(txn.transaction_type, txn) : { label: txn.transaction_type, color: '#7F8C8D', class: 'neutral', icon: '' };
    const locked = showActions && isTransactionLocked(txn);

    return (
      <div key={txn.id} className="txn-mobile-card">
        {/* Header: Type badge + Amount */}
        <div className="txn-mobile-header">
          <span className={`badge ${typeInfo.class}`}>
            {typeInfo.icon} {typeInfo.label}
          </span>
          <span className="txn-mobile-amount" style={{ color: typeInfo.color }}>
            {typeInfo.icon === '+' ? '+' : typeInfo.icon === '−' ? '−' : ''}
            {formatAmount(txn.amount)}
          </span>
        </div>

        {/* Description */}
        {txn.description && (
          <div className="txn-mobile-description">
            {txn.description}
          </div>
        )}

        {/* Meta info: Date, Category, Ledger */}
        <div className="txn-mobile-meta">
          <div className="txn-mobile-meta-item">
            <FiCalendar size={12} />
            <span>{formatDate ? formatDate(txn.transaction_date) : txn.transaction_date}</span>
          </div>
          {columns.includes('category') && txn.category_name && (
            <div className="txn-mobile-meta-item">
              <FiTag size={12} />
              <span>{txn.category_name}</span>
            </div>
          )}
          {columns.includes('ledger') && txn.ledger_name && (
            <div className="txn-mobile-meta-item">
              <FiBook size={12} />
              <span>{txn.ledger_name}</span>
            </div>
          )}
        </div>

        {/* Actions */}
        {showActions && (
          <div className="txn-mobile-actions">
            {locked ? (
              <span className="txn-locked-indicator" title="Locked (older than 24 hours)">
                <FiLock size={14} />
                <span>{toneEnabled ? 'Band hai' : 'Locked'}</span>
              </span>
            ) : (
              <>
                {onEdit && (
                  <button className="btn-icon" onClick={() => onEdit(txn)} title={t('action.edit')}>
                    <FiEdit2 size={14} />
                  </button>
                )}
                {onDelete && (
                  <button className="btn-icon danger" onClick={() => onDelete(txn)} title={t('action.delete')}>
                    <FiTrash2 size={14} />
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderDesktopRow = (txn) => {
    // Pass full transaction to getTypeInfo for direction-aware display (e.g., internal transfers)
    const typeInfo = getTypeInfo ? getTypeInfo(txn.transaction_type, txn) : { label: txn.transaction_type, color: '#7F8C8D', class: 'neutral', icon: '' };
    const locked = showActions && isTransactionLocked(txn);

    return (
      <tr key={txn.id} style={locked ? { opacity: 0.85 } : undefined}>
        {columns.includes('date') && (
          <td>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <FiCalendar size={14} style={{ color: '#BDC3C7' }} />
              {formatDate ? formatDate(txn.transaction_date) : txn.transaction_date}
            </div>
          </td>
        )}
        {columns.includes('ledger') && (
          <td>{txn.ledger_name || <span style={{ color: '#BDC3C7' }}>—</span>}</td>
        )}
        {columns.includes('category') && (
          <td>{txn.category_name || <span style={{ color: '#BDC3C7' }}>—</span>}</td>
        )}
        {columns.includes('description') && (
          <td>
            <div>{txn.description || <span style={{ color: '#BDC3C7' }}>—</span>}</div>
            {txn.notes && (
              <div className="txn-notes-preview" title={txn.notes}>
                📝 {txn.notes.length > 30 ? txn.notes.substring(0, 30) + '...' : txn.notes}
              </div>
            )}
          </td>
        )}
        {columns.includes('type') && (
          <td>
            <span className={`badge ${typeInfo.class}`}>
              {typeInfo.icon} {typeInfo.label}
            </span>
          </td>
        )}
        {columns.includes('amount') && (
          <td style={{ textAlign: columns.includes('actions') ? 'left' : 'right' }}>
            <span style={{ color: typeInfo.color, fontWeight: 600 }}>
              {typeInfo.icon === '+' ? '+' : typeInfo.icon === '−' ? '−' : ''}
              {formatAmount(txn.amount)}
            </span>
          </td>
        )}
        {columns.includes('actions') && showActions && (
          <td>
            {locked ? (
              <span className="btn-icon" title="Locked (older than 24 hours)" style={{ color: '#95a5a6', cursor: 'default' }}>
                <FiLock />
              </span>
            ) : (
              <>
                {onEdit && (
                  <button className="btn-icon" onClick={() => onEdit(txn)} title={t('action.edit')}>
                    <FiEdit2 />
                  </button>
                )}
                {onDelete && (
                  <button className="btn-icon danger" onClick={() => onDelete(txn)} title={t('action.delete')}>
                    <FiTrash2 />
                  </button>
                )}
              </>
            )}
          </td>
        )}
      </tr>
    );
  };

  const getColumnLabel = (col) => {
    const labels = {
      date: toneEnabled ? 'Tareekh' : t('transactions.date'),
      ledger: toneEnabled ? 'Khata' : t('transactions.ledger'),
      category: toneEnabled ? 'Kisam' : t('transactions.category'),
      description: toneEnabled ? 'Baat' : t('transactions.description'),
      type: toneEnabled ? 'Tarika' : t('transactions.type'),
      amount: toneEnabled ? 'Raqam' : t('transactions.amount'),
      actions: t('action.edit'),
    };
    return labels[col] || col;
  };

  return (
    <div className="txn-list-container">
      {/* Mobile Cards */}
      <div className="txn-mobile-list">
        {transactions.map(renderMobileCard)}
      </div>

      {/* Desktop Table */}
      <div className="txn-desktop-table">
        <div className="table-container" style={{ boxShadow: 'none', padding: 0 }}>
          <table className="data-table">
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={col} style={col === 'amount' && !columns.includes('actions') ? { textAlign: 'right' } : undefined}>
                    {getColumnLabel(col)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {transactions.map(renderDesktopRow)}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TransactionList;
